import * as React from 'react';
import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import type { FieldErrors, UseFormMethods } from 'react-hook-form';
import { tokens, Tooltip, Text, Button, EmailInput, Field, TextInput } from '@hotjar/hotsauce';

import { SsoErrorMessage } from '@hotjar/webapp/features/Activation/SsoError/SsoErrorMessage';
import { useSsoError } from '@hotjar/webapp/features/Activation/SsoError/useSsoError';

import { SlideInRight, SlideInLeft } from '@hotjar/webapp/utils/animations';
import { validateEmail } from '@hotjar/webapp/lib/email';

interface FormProps extends Partial<Pick<UseFormMethods, 'register'>> {
  onSubmit: (e: React.BaseSyntheticEvent) => Promise<void>;
  errors: FieldErrors;
  isDisabled: boolean;
  step: SsoSignInStep;
  apiErrorMessage: string | null;
}

interface StepProps extends Partial<Pick<UseFormMethods, 'register'>> {
  errors: FieldErrors;
}

interface TooltipWithTriggerProps {
  children?: React.ReactNode;
  text: string;
}

interface HeaderWithSubtitleProps {
  title: string;
  subTitle: React.ReactNode;
}

export enum SsoSignInStep {
  Email,
  LoginId,
}
const TABLET_BREAKPOINT = '(min-width: 768px)';
const Form = styled.form`
  margin-top: ${tokens.spacer.x2};
`;

const FormActions = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
`;

const SlideLeft = styled.div`
  @media ${TABLET_BREAKPOINT} {
    animation: ${SlideInLeft} 0.3s forwards;
  }
`;

const SlideRight = styled.div`
  @media ${TABLET_BREAKPOINT} {
    animation: ${SlideInRight} 0.3s forwards;
  }
`;

const InputContainer = styled.div`
  margin: ${tokens.spacer.x2} 0;
  width: 100%;
  @media ${TABLET_BREAKPOINT} {
    margin: ${tokens.spacer.x6} 0 ${tokens.spacer.x4} 0;
  }
`;

const TooltipTrigger = styled.span`
  border-bottom: 1px dashed ${tokens.color.text_default};
`;

const Title = styled(Text)`
  margin: 0 0 ${tokens.spacer.x1};
`;

const Subtitle = styled(Text)`
  margin: 0 0 ${tokens.spacer.x3};
`;

const HeaderWithSubtitle = ({ title, subTitle }: HeaderWithSubtitleProps): JSX.Element => {
  return (
    <>
      <Title as="h4" textType="header4" textColor="text_default">
        {title}
      </Title>
      <Subtitle as="p" textType="body1" textColor="text_light">
        {subTitle}
      </Subtitle>
    </>
  );
};

const EmailStep = ({ errors, register }: StepProps): JSX.Element => {
  const { t } = useTranslation();
  const isEmailInvalid = !!errors.email;
  return (
    <SlideLeft>
      <HeaderWithSubtitle
        title={t('activation:saml-sso.signin.sso')}
        subTitle={t('activation:saml-sso.signin.email-description')}
      />
      <InputContainer>
        <Field
          inputId="email"
          labelText={t('activation:saml-sso.signin.email-label')}
          invalidText={isEmailInvalid && errors.email.message}
          isInvalid={isEmailInvalid}
          isRequired
          size="m"
        >
          <EmailInput
            id="email"
            name="email"
            data-testid="email"
            placeholder={t('activation:saml-sso.signin.email-placeholder')}
            isInvalid={isEmailInvalid}
            autoFocus
            ref={
              register &&
              register({
                required: t('activation:saml-sso.signin.error-message-required-field') as string,
                validate: (value: string) =>
                  validateEmail(value) || (t('activation:saml-sso.signin.invalid-email') as string),
              })
            }
          />
        </Field>
      </InputContainer>
    </SlideLeft>
  );
};

const TooltipWithTrigger = ({ children, text }: TooltipWithTriggerProps): JSX.Element => (
  <Tooltip text={text}>
    <TooltipTrigger>{children}</TooltipTrigger>
  </Tooltip>
);

const LoginIdStep = ({ errors, register }: StepProps): JSX.Element => {
  const { t } = useTranslation();
  const isLoginIdInvalid = !!errors.loginId;
  return (
    <SlideRight>
      <HeaderWithSubtitle
        title={t('activation:saml-sso.signin.join-team')}
        subTitle={
          <Trans i18nKey="activation:saml-sso.signin.login-id-description">
            Enter your organization’s{' '}
            <TooltipWithTrigger text={t('activation:saml-sso.signin.login-id-help')}>
              login ID
            </TooltipWithTrigger>
          </Trans>
        }
      />
      <InputContainer>
        <Field
          inputId="loginId"
          labelText={t('activation:saml-sso.signin.login-id')}
          invalidText={isLoginIdInvalid && errors.loginId.message}
          isInvalid={isLoginIdInvalid}
          isRequired
          size="m"
        >
          <TextInput
            id="loginId"
            name="loginId"
            data-testid="loginId"
            placeholder={t('activation:saml-sso.signin.login-id-placeholder')}
            isInvalid={isLoginIdInvalid}
            autoFocus
            ref={
              register &&
              register({
                required: t('activation:saml-sso.signin.error-message-required-field') as string,
              })
            }
          />
        </Field>
      </InputContainer>
    </SlideRight>
  );
};

export const SignInForm = ({
  onSubmit,
  errors,
  register,
  isDisabled,
  step,
  apiErrorMessage,
}: FormProps): JSX.Element => {
  const { t } = useTranslation();
  const { code, apiErrorMessageKey } = useSsoError(apiErrorMessage);
  return (
    <>
      {/* Error Code error */}
      <SsoErrorMessage errorCode={code} />
      {/* Api Error */}
      <SsoErrorMessage errorCode={apiErrorMessageKey} />
      <Form onSubmit={onSubmit} noValidate>
        {step === SsoSignInStep.Email && <EmailStep errors={errors} register={register} />}
        {step === SsoSignInStep.LoginId && <LoginIdStep errors={errors} register={register} />}
        <FormActions>
          <Button type="submit" size="l" isLoading={isDisabled} isDisabled={isDisabled}>
            {t('activation:saml-sso.signin.next-step')}
          </Button>
        </FormActions>
      </Form>
    </>
  );
};
